import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentTerm, PaymentTermsService, CurrencyRatesService } from '@api-accounting-client';
import {
  CreateOrderCommand,
  OrderCommandItem,
  SalesOrdersService,
  SetOrderStatusCommand,
  POSService,
  SimpleContactDto,
  FrequentProduct,
  CDIOrderCommand,
  OrderStatusType,
  OrderType,
  ContactsService,
  ContactAddressDto,
  AddressType,
} from '@api-client'
import { AnalyzeSalesOrderResultDto } from '@api-document-intelligence';
import { ContactsDropdownComponent } from '@app/shared/components/form-fields/dropdowns/contacts-dropdown/contacts-dropdown.component';
import { DateInputDialogComponent } from '@app/shared/components/dialogs';
import { DialogService } from '@progress/kendo-angular-dialog';

import { DialogExtendedService, NotificationsExtendedService } from '../../shared/services';
import { SalesInvoiceDataSharingService, UserHelperService } from '../../shared/services/data-helper';
import { SideBarDataSharingService } from '../../shared/services/side-bar';
import { FormBaseComponent } from '../../shared/bases';
import { EditingContactData } from '../../shared/components/editing-contact/editingContactDataModel';
import { OrderEditableGridComponent } from '../../shared/components/grids/editable-grids';
import { PaymentTermsDropdownComponent } from '../../shared/components/form-fields/dropdowns/payment-terms';
import { CurrencyInfo } from '../../shared/models';
import { PrintPlatesAddonService } from '@app/shared/services/addons/print-plates-addon.service';
import { tenantNow, toTenantWallClock } from '@app/shared/helpers/tenant-time';
import {
  describeInvalidLine,
  describeSaveError,
  isReportedGlobally,
  SaveFailureMessage
} from '../../shared/helpers';

@Component({
    template: '',
    standalone: false
})
export abstract class SalesOrdersBaseComponent
  extends FormBaseComponent<any>
  implements OnInit, OnDestroy {
  @ViewChild('orderEditableGrid')
  orderEditableGrid: OrderEditableGridComponent;
  @ViewChild('paymentTermsDropdown')
  paymentTermsDropdownComponent: PaymentTermsDropdownComponent;
  @ViewChild('salesmanDropdown')
  salesmanDropdown: ContactsDropdownComponent;
  todayDate: Date = tenantNow();
  minDate = new Date(this.todayDate.toDateString());
  formArray: UntypedFormArray;
  sendCustomer = new Subject<SimpleContactDto>();
  paymentTerms: PaymentTerm[] = [];
  mode = '';

  customersFilter = {
    filters: [
      {
        field: 'isActive',
        operator: 'eq',
        value: true
      }
    ],
    logic: 'and'
  };
  salesmanFilter = {
    filters: [
      {
        field: 'isActive',
        operator: 'eq',
        value: true 
      }
    ],
    logic: 'and'
  };

  @Input() salesOrderUid: string;
  salesInvoiceUid: string;

  allowDeliverandInvoice = this.appSettingsService.generalSettings.orders.allowDeliverandInvoice;

  protected userHelperService = inject(UserHelperService);
  protected sideBarDataSharingService = inject(SideBarDataSharingService);
  protected posService = inject(POSService);
  protected dialogService = inject(DialogService);
  protected contactsService = inject(ContactsService);
  protected printPlatesAddon = inject(PrintPlatesAddonService);
  /** Print-plates add-on active for the tenant (ZEY-2932). */
  printPlatesActive = false;

  // Delivery/billing addresses for the selected customer (ZEY-452).
  contactAddresses: ContactAddressDto[] = [];
  isLoadingAddresses = false;
  actionMenuOptions = [
    {label: 'editCurrency', value: 'Edit Currency'} 
  ];

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected salesOrdersService: SalesOrdersService,
    protected paymentTermsService: PaymentTermsService,
    protected currencyRatesService: CurrencyRatesService,
    protected dialogExtendedService: DialogExtendedService,
    protected notificationsExtendedService: NotificationsExtendedService,
    protected salesInvoiceDataSharingService: SalesInvoiceDataSharingService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    // Print-plates add-on (ZEY-2932): shows the per-line "New print" checkbox.
    this.subscriptions.push(
      this.printPlatesAddon.isActive().subscribe((active) => (this.printPlatesActive = active))
    );
    this.form = this.formBuilder.group({
      contact: [null, [Validators.required]],
      salesmanUid: [null], //, [Validators.required]],
      date: [tenantNow(), [Validators.required]],
      expectedDate: [null],
      paymentTermUid: [null],
      lpo: [null, Validators.maxLength(50)],
      notes: [null],
      internalNotes: [null],
      currencyUid: [
        this.appSettingsService?.currenciesSettings?.defaultSellingCurrency,
        [Validators.required]
      ],
      currencyRate: [1, [Validators.required]],
      saveCurrencyRate: [false],
      isTaxExempt: [false],
      discountValue: [null],
      locationUid: [null],
      projectUid: [null],
      shippingAddressUid: [null],
      billingAddressUid: [null]
    });
    this.checkFormState();
    this.paymentTerms = await this.paymentTermsService
      .paymentTermsGet(
        this.utilsService.generateDataSourceRequestString(
          null,
          null,
          null,
          [{ field: 'name', dir: 'asc' }],
          1,
          500
        )
      )
      .pipe(map((x) => x.items))
      .toPromise();
    this.form.controls.paymentTermUid.setValue(this.paymentTerms?.find(
      (term) => term.numberOfDays === 0
    )?.uid);

    // On edit currency info in side drawer
    const sub = this.sideBarDataSharingService.publishDataOnClose$.subscribe(
      async (currency: CurrencyInfo) => {
        if (currency && currency.currencyUid) {
          this.form.patchValue({
            currencyUid: currency.currencyUid,
            currencyRate: currency.currencyRate,
            saveCurrencyRate: currency.saveCurrencyRate
          });
        }
      }
    );
    this.subscriptions.push(sub);

    // Check for document intelligence data passed via router state
    this.checkForDocumentData();
  }

  /**
   * Check for document intelligence data passed via router state
   * and pre-fill the form if available
   */
  private checkForDocumentData(): void {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state as { documentData: AnalyzeSalesOrderResultDto };

    if (state?.documentData) {
      this.prefillFormFromDocument(state.documentData);
    } else {
      // Also check history.state for page refreshes
      const historyState = history.state as { documentData: AnalyzeSalesOrderResultDto };
      if (historyState?.documentData) {
        this.prefillFormFromDocument(historyState.documentData);
      }
    }
  }

  /**
   * Pre-fill the sales order form with data extracted from a document
   */
  private prefillFormFromDocument(data: AnalyzeSalesOrderResultDto): void {
    // Pre-fill form fields
    if (data.date) {
      this.form.patchValue({ date: new Date(data.date) });
    }

    if (data.expectedDate) {
      this.form.patchValue({ expectedDate: new Date(data.expectedDate) });
    }

    if (data.lpo) {
      this.form.patchValue({ lpo: data.lpo });
    }

    if (data.currencyUid) {
      this.form.patchValue({ currencyUid: data.currencyUid });
    }

    if (data.currencyRate) {
      this.form.patchValue({ currencyRate: data.currencyRate });
    }

    // Pre-fill customer/contact - needs to be done after view init
    if (data.contactUid || data.contactName) {
      setTimeout(() => {
        if (data.contactUid) {
          this.form.patchValue({ contact: { uid: data.contactUid, fullName: data.contactName } });
          this.getContactAddresses(data.contactUid);
        }
      }, 100);
    }

    // Pre-fill line items - needs to be done after the grid is initialized
    if (data.items && data.items.length > 0) {
      setTimeout(() => {
        this.prefillLineItems(data.items);
      }, 500);
    }
  }

  /**
   * Pre-fill line items in the order grid
   */
  private prefillLineItems(items: any[]): void {
    if (!this.orderEditableGrid) {
      return;
    }

    items.forEach(item => {
      if (item.productUid || item.productName) {
        // const orderItem: OrderCommandItem = {
        //   productUid: item.productUid,
        //   quantity: item.quantity || 1,
        //   unitPrice: item.unitPrice || 0,
        //   unitUid: item.unitUid,
        //   unitRate: item.unitRate || 1,
        //   discountPercentage: item.discountPercentage || 0,
        //   taxGroupUid: item.taxGroupUid,
        //   note: item.note
        // };
        // Add to grid - the grid should have an addItem method or similar
        // if (this.orderEditableGrid.addProductToGrid) {
        //   this.orderEditableGrid.addProductToGrid({
        //     uid: item.productUid,
        //     name: item.productName,
        //     code: item.productCode
        //   }, orderItem.quantity, orderItem.unitPrice);
        // }
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onDateChange(date: string | null) {
    this.minDate = new Date(new Date(date ?? this.todayDate).toDateString());
    const { paymentTermUid } = this.form.controls;

    if (!date) {
      paymentTermUid.setValue(null);
      return;
    }

  }

  onLoadData(): any {
    return {};
  }

  sendCustomerToEditContact(value: SimpleContactDto): void {
    this.sendCustomer.next(value);
    // Customer changed -> reload its addresses and clear any prior selection (ZEY-452).
    this.form.patchValue({ shippingAddressUid: null, billingAddressUid: null });
    this.getContactAddresses(value?.uid);
  }

  public async getContactAddresses(contactUid?: string): Promise<void> {
    if (!contactUid) {
      this.contactAddresses = [];
      return;
    }
    this.isLoadingAddresses = true;
    try {
      const { items } = await this.contactsService
        .contactsContactAddressesUidGet(contactUid)
        .toPromise();
      this.contactAddresses = [...(items ?? [])];
    } finally {
      this.isLoadingAddresses = false;
    }
  }

  get billingAddresses(): ContactAddressDto[] {
    return this.contactAddresses?.filter(
      (address) =>
        address.addressType === AddressType.BillingAddress ||
        address.addressType === AddressType.ShippingAndBillingAddress
    );
  }

  get shippingAddresses(): ContactAddressDto[] {
    return this.contactAddresses?.filter(
      (address) =>
        address.addressType === AddressType.ShippingAddress ||
        address.addressType === AddressType.ShippingAndBillingAddress ||
        // A shop is a place you deliver to, so it belongs in the shipping
        // picker — but not the billing one (ZEY-1683).
        address.addressType === AddressType.Shop
    );
  }

  onSalesmanReceived(editingContactData: EditingContactData): void {
    const nowPaymentTerm = this.paymentTerms?.find(term => term.numberOfDays === 0);

    if (editingContactData === null) {
      this.form.controls.salesmanUid.setValue(null);
      this.form.controls.paymentTermUid.setValue(nowPaymentTerm ? nowPaymentTerm.uid : null, true);
      return;
    }

    this.form.controls.paymentTermUid.setValue(
      editingContactData.paymentTermUid ? editingContactData.paymentTermUid : nowPaymentTerm ? nowPaymentTerm.uid : null
    );
    
    let salesman: any = editingContactData.salesmanUid;
    const { salesmanUid } = this.form.controls;

    if (salesman) {
      // If Customer has a Salesman
      salesmanUid.setValue(salesman);
    }
    else {
      // Set logged in user as Salesman
      const user = this.userHelperService.getData();
      salesman = {
        uid: user.contactUid,
        fullName: user.fullName
      };

      salesmanUid.setValue(salesman.uid);
      this.salesmanDropdown.selectedItem = salesman;
      this.salesmanDropdown.handleInitiallySelectedValue();
      setTimeout(() => {
        this.salesmanDropdown.items = [...this.salesmanDropdown.items];
      }, 0);
    }
  }

  /**
   * ZEY-1896: a chance to stop the save when the edit would release supply pegged to a line.
   * Defaults to "nothing to warn about" — a brand-new order cannot have pegs yet — and is
   * overridden by the edit screen, which is the only one that can.
   *
   * @returns true to go ahead with the save, false to return the user to the form.
   */
  protected async confirmSupplyImpact(_items: OrderCommandItem[]): Promise<boolean> {
    return true;
  }

  abstract onDataSubmit<TCommand>(command: TCommand): Observable<any>;

  abstract onStatusSubmit<TCommand extends unknown>(
    status: TCommand
  ): Observable<any>;

  goToListPage(tab?: 'quotations'): void {
    this.router.navigate(['/sales-orders'], tab ? { queryParams: { tab } } : {});
  }

  goToAddPage(): void {
    this.router.navigate(['/sales-orders/add']);
  }

  goToViewPage(event: any): void {
    this.router.navigate(['/sales-orders/view'], {
      queryParams: { id: event }
    });
  }

  protected checkFormState(): void {
    this.form.valueChanges.subscribe(() => {
      if (!this.form.dirty) {
        this.isDirty = false;
      } else {
        this.isDirty = true;
      }
    });
  }

  dirtyFormHandler(isDirty: boolean): void {
    if (isDirty) {
      this.isDirty = true;
    }
  }

  /**
   * ZEY-2119: report a failed save — unless the global interceptor already has, in which
   * case a second dialog would land on top of the first.
   */
  protected showSaveError(error: unknown): void {
    if (isReportedGlobally(error)) {
      return;
    }
    this.showSaveFailure(describeSaveError(error));
  }

  /**
   * ZEY-2119 / ZEY-2120: a refused save used to return in silence — no dialog, no
   * toast, not even a spinner, because `isWaiting` is only set further down. Users
   * reported that as the screen freezing. Every exit that abandons a save now says why.
   */
  protected showSaveFailure(message: SaveFailureMessage): void {
    this.dialogExtendedService.showValidationDialog(
      this.translate.instant(message.key, message.params)
    );
  }

  async submitRecord(status: string, _mode?: string, _actionName?: string): Promise<void> {
    super.submit();

    if (!this.orderEditableGrid) {
      this.dialogExtendedService.showValidationDialog(
        'yourFormIsNotValid'
      );
      return;
    }

    this.formArray = this.orderEditableGrid.getData();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showSaveFailure({ key: 'formHeaderIsIncomplete' });
      return;
    }

    for (const [index, form] of (
      this.formArray.controls?.slice(0, -1) ?? []
    ).entries()) {
      if (form.invalid) {
        form.markAllAsTouched();
        this.showSaveFailure(
          describeInvalidLine(form as UntypedFormGroup, index + 1, (key) =>
            this.translate.instant(key)
          )
        );
        return;
      }
      // Checking if marking order as Confirm and any of the line item has missing product
      if (status === OrderStatusType.Confirmed && !form.value.uid) {
        // Product is missing because of Note field is filled instead
        this.dialogExtendedService.showValidationDialog(
          'allLineItemsMustHaveProductsSelectedToMarkOrderAsConfirm'
        );
        return;
      }
    }

    const items: OrderCommandItem[] = [];
    let item: OrderCommandItem;

    for (const form of (this.formArray.controls as UntypedFormGroup[])?.slice(0, -1)) {
      // Get form group value regardless of disabled controls
      const value = form.getRawValue();
      item = {
        uid: value.itemUid,
        productUid: value.uid,
        note: value.note,
        unitUid: value.unit,
        unitRate: value.unitRate,
        discountPercentage: Number((value.discount / 100).toFixed(4)),
        quantity: value.quantity,
        unitPrice: value.unitPrice,
        taxGroupUid: value.taxGroupUid,
        type:OrderType.Sales,
        // Sent only with the add-on on: true writes, false clears, undefined (null) leaves alone.
        // Saved lines carry their current flag, so an edit that does not touch it keeps it.
        newPrint: this.printPlatesActive ? !!value.newPrint : undefined
      };
      if (item.quantity === 0) {
        this.dialogExtendedService.showValidationDialog(
          'quantityCanNotBe0PleaseAddSomeValue'
        );
        return;
      }
      items.push(item);
    }

    if (items.length === 0) {
      this.dialogExtendedService.showValidationDialog(
        'youHaveNoProductsOrNotesAdded'
      );
      return;
    }

    // ZEY-1896: last gate before the PUT. An edit that shrinks or drops a line with supply pegged
    // to it silently gives that supply back (the server trims the pegs on save), and the purchase
    // or manufacturing order stays open with nobody's name on it — so the user is told first.
    // Add and quote screens inherit the no-op default; only the edit screen has links to check.
    if (!(await this.confirmSupplyImpact(items))) {
      return;
    }

    this.isWaiting = true;
    const cmd: any = this.mapControlsToModel<CreateOrderCommand>();
    const command = {
      ...cmd,
      contactUid: typeof cmd.contact === 'string' ? cmd.contact : cmd.contact.uid,
      contact: undefined
    };
    command.items = items;
    command.type = OrderType.Sales;
    command.status = OrderStatusType.Draft;

     
    this.orderEditableGrid.discountMethod !== '%'
      ? (command.discountAmount =
        this.orderEditableGrid.discountValue ?? 0)
      : (command.discountPercentage =
          Number((((this.orderEditableGrid.discountValue ?? 0) / 100).toFixed(4)))
      );

    try {
      const response = await this.onDataSubmit(command).toPromise();

      this.salesOrderUid = this.salesOrderUid ? this.salesOrderUid : response.uid;
      this.isWaiting = false;
      this.isDirty = false;

      if (status != OrderStatusType.Draft) {
        this.isWaiting = true;
        const command: SetOrderStatusCommand = {};
        command.status = status === OrderStatusType.Confirmed
          ? (command.status = OrderStatusType.Confirmed)
          : (command.status = OrderStatusType.Closed);
        const response = await this.onStatusSubmit(command).toPromise();
        this.salesInvoiceUid = response.invoiceUid;
        this.isWaiting = false;
      }

      // ZEY-2238: a sales order goes back to Sales Orders after Save and Close, in add
      // mode as well as edit mode. Add mode used to open /quotes, the separate Quotations
      // screen. That screen is menu-gated, so a role without it (gourmand's Accountant)
      // landed on "Access Denied" right after a successful save. Quotations are saved
      // from their own form (QuotationsFormBaseComponent), which still returns to the
      // page it was opened from.
      //
      // A new order is saved as a draft, which the list shows under its Quotations tab, not
      // under Confirmed (the tab it opens on). Open that tab so the order just saved is on
      // screen, instead of looking as if it was never saved.
      if (_actionName == 'Save and Close') {
        this.goToListPage(_mode === 'add' ? 'quotations' : undefined);
      }

      if (_actionName == 'Save') {
        if (_mode === 'edit') {
         this.appSettingsService.navigateBack();
        }
      }

      if (_actionName == 'Save and New') {
        if (_mode === 'add') {
          this.notificationsExtendedService.showNotification(
            this.translate.instant('salesOrderHasBeenCreated')
          );
          this.reloadRoute();
        } else if (_mode === 'edit') {
          this.goToAddPage();
        }
      }

      if (_actionName == 'Confirm' && status === OrderStatusType.Confirmed && this.salesOrderUid) {
        this.goToViewPage(this.salesOrderUid);
      }
    } catch (error: any) {
      this.isWaiting = false;
      this.isDirty = false;   

      if (error.status === 403) {
        this.dialogExtendedService.showNotAuthorizedDialog();
        return;
      }

      // ZEY-2119: 403 aside, every status fell through to a bare return here.
      this.showSaveError(error);
      return;
    }
  }

  private async deliverAndInvoice(): Promise<void> {
    super.submit();

    if (!this.orderEditableGrid) {
      this.dialogExtendedService.showValidationDialog(
        'yourFormIsNotValid'
      );
      return;
    }

    this.formArray = this.orderEditableGrid.getData();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showSaveFailure({ key: 'formHeaderIsIncomplete' });
      return;
    }

    for (const [index, form] of (
      this.formArray.controls?.slice(0, -1) ?? []
    ).entries()) {
      if (form.invalid) {
        form.markAllAsTouched();
        this.showSaveFailure(
          describeInvalidLine(form as UntypedFormGroup, index + 1, (key) =>
            this.translate.instant(key)
          )
        );
        return;
      }
      // Checking if marking order as Deliver and Invoice and any of the line item has missing product
      if (!form.value.uid) {
        // Product is missing because of Note field is filled instead
        this.dialogExtendedService.showValidationDialog(
          'allLineItemsMustHaveProductsSelectedToMarkOrderAsDeliverAndInvoice'
        );
        return;
      }
    }

    const items: OrderCommandItem[] = [];
    let item: OrderCommandItem;

    for (const form of (this.formArray.controls as UntypedFormGroup[])?.slice(0, -1)) {
      const formValue = form.getRawValue();
      item = {
        uid: formValue.itemUid,
        productUid: formValue.uid,
        note: formValue.note,
        unitUid: formValue.unit,
        unitRate: formValue.unitRate,
        discountPercentage: Number((formValue.discount / 100).toFixed(4)),
        quantity: formValue.quantity,
        unitPrice: form.controls.unitPrice.value,
        taxGroupUid: form.value.taxGroupUid,
        type:OrderType.Sales
      };
      if (item.quantity === 0) {
        this.dialogExtendedService.showValidationDialog(
          'quantityCanNotBe0PleaseAddSomeValue'
        );
        return;
      }
      items.push(item);
    }

    if (items.length === 0) {
      this.dialogExtendedService.showValidationDialog(
        'youHaveNoProductsOrNotesAdded'
      );
      return;
    }

    const cmd: any = this.mapControlsToModel<CDIOrderCommand>();
    const command = {
      ...cmd,
      contactUid: cmd.contact.uid,
      contact: undefined
    };
    command.items = items;
    command.type = OrderType.Sales;
    command.status = OrderStatusType.Confirmed;

    this.orderEditableGrid.discountMethod !== '%'
      ? (command.discountAmount =
        this.orderEditableGrid.discountValue ?? 0)
      : (command.discountPercentage =
        Number((((this.orderEditableGrid.discountValue ?? 0) / 100).toFixed(4))));

    // ZEY-407: prompt for the invoice date before the one-shot deliver-and-invoice. This button
    // (order creation/edit screen) previously never asked and silently used today's date — the
    // sales-order VIEW page already prompts (ZEY-276), so this closes the gap. The backend CDI
    // handler applies command.Date to both the delivery and the SalesInvoice.Date
    // (CDIOrderCommandHandler ReceivedDate = InvoicedDate = Date), so the chosen date becomes the
    // invoice date. Defaults to the order date already on the form.
    const dateDialogRef = this.dialogService.open({
      content: DateInputDialogComponent,
      width: 500
    });
    dateDialogRef.content.instance.title = 'Deliver and Invoice Date';
    dateDialogRef.content.instance.isAllowFutureDate = true;
    dateDialogRef.content.instance.defaultDate = command.date
      ? toTenantWallClock(command.date)
      : tenantNow();

    dateDialogRef.result.subscribe(async (data: any) => {
      if (!data?.date) {
        // Dialog dismissed/cancelled — do not create the order or invoice.
        return;
      }
      command.date = data.date;

      this.isWaiting = true;
      // ZEY-212: the backend create-deliver-invoice is now atomic, so a failed attempt leaves no
      // order behind. Reset isWaiting and surface the error on failure — previously the spinner
      // stayed stuck and gave no feedback, prompting users to refresh and resubmit, which created a
      // duplicate confirmed order. The try/finally also re-enables the button so a clean retry works.
      try {
        const response = await this.salesOrdersService
          .salesOrdersDeliverAndInvoiceFullPost(command).toPromise();
        this.isDirty = false;

        this.router.navigate(['/sales-invoices/view'], {
          queryParams: { id: response.invoiceUid }
        });
      } catch (error: any) {
        if (error?.status === 403) {
          this.dialogExtendedService.showNotAuthorizedDialog();
        } else {
          this.showSaveError(error);
        }
      } finally {
        this.isWaiting = false;
      }
    });
  }


  onTaxExemptValueChange(isTaxExempt: boolean): void {
    this.salesInvoiceDataSharingService.sendIsTaxExemptData(isTaxExempt);
  }

  openCurrencyInfoAddDrawer() {
    const { currencyUid, currencyRate } = this.form.value;
    this.sideBarDataSharingService.sendCurrencyInfoData({
      currencyUid: currencyUid,
      currencyRate: currencyRate,
      mode: this.mode,
      date: this.form.value.date
    });
  }

  /**
   * Populate line item based on line item which was
   * previously orderered frequently, in a sales invoice
   * @param data 
   */
  async addLineItem(data: FrequentProduct) {
    const index = this.orderEditableGrid.getData().length - 1;
    const gridRecord: any = {
      rowId: index,
      itemUid: null,
      discount: 0,
      code: data.code,
      uid: data.uid,
      name: data.name,
      unitPrice: null,
      note: data.note,
      quantity: data.quantity,
      unit: data.unitUid,
      unitSymbol: data.unitSymbol,
      baseUnitUid: data.unitUid,
      unitRate: 1,
      baseUnitPrice: 0,
      taxGroupUid: data.taxGroupUid,
      net: null
    };
    
    // Fill the product in products dropdown array
    this.orderEditableGrid.prepopulateProducts(gridRecord);
    
    // Get last formGroup of orderEditableGrid
    const formGroup = this.orderEditableGrid
      .getData()
      .at(index) as UntypedFormGroup;
    // Set the info in formGroup
    formGroup.patchValue(gridRecord);

    this.orderEditableGrid.filterUnitsByProductUnits(index);

    await this.orderEditableGrid.getProductUnitPrice(
      gridRecord.uid,
      index,
      data.forceMinPrice,
      data.minimumSalesPrice
    );

    // Calculate line item Net Amount
    this.orderEditableGrid.calculateNetAmount(index);
  }

  onActionClick(value: any, _mode?: string) {
    switch (value) {
      case 'Save and Close':
        this.submitRecord(OrderStatusType.Draft, _mode, value);
        break;
      case 'Save and New':
        this.submitRecord(OrderStatusType.Draft, _mode, value);
        break;
      case 'Save':
        this.submitRecord(OrderStatusType.Draft, _mode, value);
        break;
      case 'Cancel':
        this.appSettingsService.navigateBack();
        break;
      case 'Discard':
        this.goToListPage();
        break;
      case 'Confirm':
        this.submitRecord(OrderStatusType.Confirmed, _mode, value);
        break;
      case 'Deliver and Invoice':
        this.deliverAndInvoice();
        break;
    }
  }

  @HostListener('document:keydown.escape') onEscapeHandler(): void {
    if (!this.dialogExtendedService.closeValidationDialog()) {

      if (!this.isDrawerOpened) {
        this.goToListPage();
      }

    }
  }
}
